import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaSync, FaExternalLinkAlt } from "react-icons/fa";
import { FiTrendingUp, FiClock, FiZap, FiCheckCircle, FiActivity } from "react-icons/fi";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import DashboardLayout from "../Dashboard/DashboardLayout";
import { getExecutionMetrics, type ExecutionMetrics } from "../../api/executions";
import FabricMonitorTab from "./FabricMonitorTab";
import FabricLineageTab from "./FabricLineageTab";
import AuditLogTab from "./AuditLogTab";
import ComplianceTab from "./ComplianceTab";
import DriftTab from "./DriftTab";
import RuntimeTab from "./RuntimeTab";

const TABS = [
  { key: 'cicd',       label: '🚀 CI/CD Terraform' },
  { key: 'fabric',     label: '⚡ Microsoft Fabric' },
  { key: 'lineage',    label: '🔀 Data Lineage' },
  { key: 'audit',      label: '📋 Audit Log' },
  { key: 'compliance', label: '🛡️ Conformité' },
  { key: 'runtime',    label: '⏱ Runtime' },
  { key: 'drift',      label: '🔍 Drift & Gouvernance' },
] as const;

type TabKey = typeof TABS[number]['key'];

const MonitoringPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('cicd');
  const [metrics, setMetrics] = useState<ExecutionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<7 | 30 | 90>(30);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 60000);
    return () => clearInterval(interval);
  }, [period]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getExecutionMetrics({ days: period });
      setMetrics(data as ExecutionMetrics);
    } catch (err) {
      console.error("Failed to load metrics:", err);
      setError("Erreur lors du chargement des métriques");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
  };

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  const STATUS_STYLES: Record<string, { bg: string; color: string; dot: string }> = {
    succeeded: { bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
    failed:    { bg: '#FEF2F2', color: '#991B1B', dot: '#EF4444' },
    running:   { bg: '#EFF6FF', color: '#1E40AF', dot: '#3B82F6' },
    queued:    { bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
    pending:   { bg: '#F9FAFB', color: '#374151', dot: '#9CA3AF' },
    cancelled: { bg: '#F9FAFB', color: '#6B7280', dot: '#9CA3AF' },
  };

  const ACTION_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
    plan:    { bg: '#EFF6FF', color: '#1E40AF', icon: '📋' },
    apply:   { bg: '#ECFDF5', color: '#065F46', icon: '🚀' },
    destroy: { bg: '#FEF2F2', color: '#991B1B', icon: '🗑️' },
  };

  const ENV_STYLES: Record<string, { bg: string; color: string }> = {
    prod:    { bg: '#FEF2F2', color: '#991B1B' },
    staging: { bg: '#FEF3C7', color: '#92400E' },
    dev:     { bg: '#ECFDF5', color: '#065F46' },
  };

  const successRate = metrics ? metrics.global.success_rate : 0;
  const isGoodRate = successRate >= 80;

  return (
    <DashboardLayout>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#111827', margin: '0 0 0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              📊 Monitoring
            </h1>
            <p style={{ color: '#6B7280', fontSize: '0.875rem', margin: 0 }}>
              CI/CD Terraform &amp; Capacités Microsoft Fabric
            </p>
          </div>

          {activeTab === 'cicd' && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{ display: 'flex', background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.625rem', padding: '3px', gap: '2px' }}>
                {([7, 30, 90] as const).map(days => (
                  <button
                    key={days}
                    onClick={() => setPeriod(days)}
                    style={{
                      padding: '0.35rem 1rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: period === days ? 700 : 500,
                      background: period === days ? '#1e40af' : 'transparent',
                      color: period === days ? 'white' : '#6B7280',
                      transition: 'all 0.15s',
                    }}
                  >
                    {days}j
                  </button>
                ))}
              </div>
              <button
                onClick={loadMetrics}
                disabled={loading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.5rem 1rem',
                  background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.625rem',
                  fontSize: '0.82rem', fontWeight: 500, color: '#374151',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <FaSync style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} size={12} />
                Actualiser
              </button>
            </div>
          )}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', gap: 3, marginBottom: '1.5rem', background: '#F3F4F6', padding: 4, borderRadius: 12, overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '0.45rem 1.1rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: activeTab === tab.key ? 700 : 500,
                background: activeTab === tab.key ? 'white' : 'transparent',
                color: activeTab === tab.key ? '#111827' : '#6B7280',
                boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Sous-onglets ── */}
        {activeTab === 'fabric'     && <FabricMonitorTab />}
        {activeTab === 'lineage'    && <FabricLineageTab />}
        {activeTab === 'audit'      && <AuditLogTab />}
        {activeTab === 'compliance' && <ComplianceTab />}
        {activeTab === 'runtime'    && <RuntimeTab />}
        {activeTab === 'drift'      && <DriftTab />}

        {/* ── CI/CD ── */}
        {activeTab === 'cicd' && (
          <>
            {/* Loading */}
            {loading && !metrics && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5rem 0', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ width: 48, height: 48, border: '3px solid #e5e7eb', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Chargement des métriques CI/CD...</p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#991B1B', fontSize: '0.875rem' }}>{error}</span>
                <button onClick={loadMetrics} style={{ background: '#EF4444', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                  Réessayer
                </button>
              </div>
            )}

            {metrics && (
              <>
                {/* ── KPI Cards ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>

                  {/* Total */}
                  <div style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3B82F6 100%)',
                    borderRadius: '1rem', padding: '1.5rem', color: 'white',
                    boxShadow: '0 4px 16px rgba(59,130,246,0.3)',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Total Exécutions</span>
                      <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem', fontSize: '1rem' }}>🚀</div>
                    </div>
                    <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{metrics.global.total}</div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>Période de {period} jours</div>
                  </div>

                  {/* Success Rate */}
                  <div style={{
                    background: isGoodRate
                      ? 'linear-gradient(135deg, #065F46 0%, #10B981 100%)'
                      : 'linear-gradient(135deg, #92400E 0%, #F59E0B 100%)',
                    borderRadius: '1rem', padding: '1.5rem', color: 'white',
                    boxShadow: isGoodRate ? '0 4px 16px rgba(16,185,129,0.3)' : '0 4px 16px rgba(245,158,11,0.3)',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Taux de Succès</span>
                      <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}>
                        <FiCheckCircle size={16} color="white" />
                      </div>
                    </div>
                    <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                      {metrics.global.success_rate.toFixed(1)}%
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>
                      {metrics.global.succeeded} / {metrics.global.total} réussis
                    </div>
                    <div style={{ marginTop: '0.75rem', height: 4, background: 'rgba(255,255,255,0.25)', borderRadius: 9999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(successRate, 100)}%`, background: 'rgba(255,255,255,0.8)', borderRadius: 9999, transition: 'width 0.8s ease' }} />
                    </div>
                  </div>

                  {/* Avg Duration */}
                  <div style={{
                    background: 'linear-gradient(135deg, #6d28d9 0%, #8B5CF6 100%)',
                    borderRadius: '1rem', padding: '1.5rem', color: 'white',
                    boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Durée Moyenne</span>
                      <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}>
                        <FiClock size={16} color="white" />
                      </div>
                    </div>
                    <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                      {formatDuration(metrics.global.avg_duration_seconds)}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>
                      Médiane : {formatDuration(metrics.global.median_duration_seconds)}
                    </div>
                  </div>

                  {/* In Progress / Failed */}
                  <div style={{
                    background: metrics.global.failed > 0
                      ? 'linear-gradient(135deg, #991B1B 0%, #EF4444 100%)'
                      : 'linear-gradient(135deg, #b45309 0%, #F59E0B 100%)',
                    borderRadius: '1rem', padding: '1.5rem', color: 'white',
                    boxShadow: metrics.global.failed > 0 ? '0 4px 16px rgba(239,68,68,0.3)' : '0 4px 16px rgba(245,158,11,0.3)',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>En Cours</span>
                      <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}>
                        <FiActivity size={16} color="white" />
                      </div>
                    </div>
                    <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                      {metrics.global.in_progress}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>
                      {metrics.global.failed} échec{metrics.global.failed > 1 ? 's' : ''} sur la période
                    </div>
                  </div>
                </div>

                {/* ── Charts Row 1 ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

                  <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e5e7eb', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                      <FiTrendingUp size={18} color="#3B82F6" />
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: 0 }}>Tendances des Déploiements</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={[...metrics.trends].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                        <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                        <Tooltip contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb', fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2.5} dot={false} name="Total" />
                        <Line type="monotone" dataKey="succeeded" stroke="#10B981" strokeWidth={2.5} dot={false} name="Succès" />
                        <Line type="monotone" dataKey="failed" stroke="#EF4444" strokeWidth={2.5} dot={false} name="Échecs" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e5e7eb', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                      <FiZap size={18} color="#8B5CF6" />
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: 0 }}>Répartition par Action</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={metrics.by_action}
                          cx="50%" cy="50%"
                          labelLine={false}
                          label={({ payload }: any) => `${payload?.action || ''}: ${payload?.count || 0}`}
                          outerRadius={100}
                          dataKey="count"
                        >
                          {metrics.by_action.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb', fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* ── Charts Row 2 ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

                  <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e5e7eb', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      🌍 Déploiements par Environnement
                    </h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={metrics.by_environment} barSize={24}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                        <Tooltip contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb', fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="count" fill="#3B82F6" name="Total" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="succeeded" fill="#10B981" name="Succès" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top Architectures */}
                  <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e5e7eb', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', marginBottom: '1.25rem' }}>
                      🏆 Top Architectures Déployées
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {metrics.top_architectures.slice(0, 5).map((arch, index) => {
                        const rate = arch.count > 0 ? (arch.succeeded / arch.count) * 100 : 0;
                        return (
                          <div key={arch.id} style={{
                            display: 'flex', alignItems: 'center', gap: '0.875rem',
                            padding: '0.75rem 1rem',
                            background: '#F9FAFB',
                            borderRadius: '0.625rem',
                            border: '1px solid #f3f4f6',
                          }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#D1D5DB', minWidth: '1.5rem' }}>
                              #{index + 1}
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {arch.name}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                                <div style={{ flex: 1, height: 4, background: '#E5E7EB', borderRadius: 9999, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${rate}%`, background: rate >= 80 ? '#10B981' : rate >= 50 ? '#F59E0B' : '#EF4444', borderRadius: 9999 }} />
                                </div>
                                <span style={{ fontSize: '0.72rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                  {rate.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#10B981' }}>{arch.succeeded}✓</span>
                              {arch.failed > 0 && <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#EF4444', marginLeft: '0.4rem' }}>{arch.failed}✗</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── Recent Executions ── */}
                <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #f3f4f6' }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                      🕒 Exécutions Récentes
                    </h3>
                    <button
                      onClick={() => navigate('/executions')}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.875rem', background: '#EFF6FF', color: '#1e40af', border: 'none', borderRadius: '0.5rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Voir tout <FaExternalLinkAlt size={10} />
                    </button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          {['Run ID', 'Action', 'Architecture', 'Environnement', 'Statut', 'Durée', 'Date'].map(h => (
                            <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.recent_executions.slice(0, 10).map((exec, idx) => {
                          const statusStyle = STATUS_STYLES[exec.status] || STATUS_STYLES.pending;
                          const actionStyle = ACTION_STYLES[exec.action] || { bg: '#F3F4F6', color: '#374151', icon: '▶' };
                          const envStyle = ENV_STYLES[exec.environment_type || ''] || { bg: '#F9FAFB', color: '#6B7280' };
                          return (
                            <tr
                              key={exec.run_id}
                              onClick={() => navigate(`/executions/${exec.run_id}`)}
                              style={{ cursor: 'pointer', borderBottom: '1px solid #F9FAFB', background: idx % 2 === 0 ? 'white' : '#FAFAFA', transition: 'background 0.1s' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#F0F9FF')}
                              onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#FAFAFA')}
                            >
                              <td style={{ padding: '0.875rem 1rem' }}>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#3B82F6', fontWeight: 600 }}>
                                  {exec.run_id.substring(0, 8)}…
                                </span>
                              </td>
                              <td style={{ padding: '0.875rem 1rem' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.2rem 0.6rem', borderRadius: '0.375rem', background: actionStyle.bg, color: actionStyle.color, fontSize: '0.75rem', fontWeight: 700 }}>
                                  {actionStyle.icon} {exec.action}
                                </span>
                              </td>
                              <td style={{ padding: '0.875rem 1rem', fontSize: '0.85rem', color: '#111827', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {exec.architecture_name || '—'}
                              </td>
                              <td style={{ padding: '0.875rem 1rem' }}>
                                {exec.environment_name && (
                                  <span style={{ padding: '0.2rem 0.6rem', borderRadius: '0.375rem', background: envStyle.bg, color: envStyle.color, fontSize: '0.75rem', fontWeight: 600 }}>
                                    {exec.environment_name}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '0.875rem 1rem' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.2rem 0.6rem', borderRadius: '0.375rem', background: statusStyle.bg, color: statusStyle.color, fontSize: '0.75rem', fontWeight: 700 }}>
                                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusStyle.dot, flexShrink: 0 }} />
                                  {exec.status}
                                </span>
                              </td>
                              <td style={{ padding: '0.875rem 1rem', fontSize: '0.82rem', color: '#6B7280' }}>
                                {exec.duration_seconds ? formatDuration(exec.duration_seconds) : '—'}
                              </td>
                              <td style={{ padding: '0.875rem 1rem', fontSize: '0.82rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                {new Date(exec.created_at).toLocaleString('fr-FR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </DashboardLayout>
  );
};

export default MonitoringPage;
